package pl.mothdigital.neuronowi.pages.specialists

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.resources.*
import pl.mothdigital.neuronowi.components.*
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.theme.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.css.AlignItems
import pl.mothdigital.neuronowi.common.model.SpecialistData

private val priceItems = listOf(
    PriceItem(
        StringResources.MAGDALENA_BANACH_PRICE_1_NAME,
        StringResources.MAGDALENA_BANACH_PRICE_1_TIME,
        StringResources.MAGDALENA_BANACH_PRICE_1_AMOUNT
    ),
    PriceItem(
        StringResources.MAGDALENA_BANACH_PRICE_2_NAME,
        StringResources.MAGDALENA_BANACH_PRICE_2_TIME,
        StringResources.MAGDALENA_BANACH_PRICE_2_AMOUNT
    ),
    PriceItem(
        StringResources.MAGDALENA_BANACH_PRICE_3_NAME,
        StringResources.MAGDALENA_BANACH_PRICE_3_TIME,
        StringResources.MAGDALENA_BANACH_PRICE_3_AMOUNT
    )
)

private val aboutMeParagraphs = listOf(
    StringResources.MAGDALENA_BANACH_ABOUT_ME_1,
    StringResources.MAGDALENA_BANACH_ABOUT_ME_2,
    StringResources.MAGDALENA_BANACH_ABOUT_ME_3,
    StringResources.MAGDALENA_BANACH_ABOUT_ME_4,
    StringResources.MAGDALENA_BANACH_ABOUT_ME_5,
    StringResources.MAGDALENA_BANACH_ABOUT_ME_6,
)

@Page
@Composable
fun MagdalenaBanachPage() {
    SpecialistPageLayout(
        title = StringResources.MAGDALENA_BANACH,
        headerSection = { HeaderSection() },
        profileSection = { ProfileSection() },
        specializationsSection = { SpecializationsSection() },
        servicesSection = { ServicesSection() },
        priceSection = { PriceSection() },
        ctaSection = { context ->
            CTASection(
                ctx = context,
                customAction = { js("window.scrollTo({ top: 0, behavior: 'smooth' })") },
            )
        },
    )
}

@Composable
private fun HeaderSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().margin { auto },
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH1 { Text(StringResources.MAGDALENA_BANACH.fixPolishOrphans()) }
            AppP(color = SiteTheme.green._800, modifier = Modifier.fontSize(1.25.cssRem)) {
                Text(StringResources.MAGDALENA_BANACH_ACADEMIC_TITLES.fixPolishOrphans())
            }
            AppP(
                color = SiteTheme.green._800,
                modifier = Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold)
            ) {
                Text(StringResources.MAGDALENA_BANACH_PHONE_NUMBER)
            }
        }
    }
}

@Composable
private fun ProfileSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        // Large screens: side-by-side image and text
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexWrap(FlexWrap.Wrap)
                .displayIfAtLeast(Breakpoint.LG)
                .gap(3.cssRem),
        ) {
            Image(
                src = ImageResources.avatarMagdalenaBanach.src,
                alt = ImageResources.avatarMagdalenaBanach.description,
                modifier = Modifier
                    .width(400.px)
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
            Column(
                modifier = Modifier
                    .flex(1)
                    .gap(1.5.cssRem)
                    .padding(1.cssRem),
            ) {
                AppH2 { Text("O mnie") }
                AboutMeParagraphs()
            }
        }
        // Medium and smaller: image above text
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .displayUntil(Breakpoint.LG)
                .gap(1.5.cssRem)
                .padding(1.cssRem),
        ) {
            AppH2 { Text("O mnie") }
            AboutMeParagraphs()
            Image(
                src = ImageResources.avatarMagdalenaBanach.src,
                alt = ImageResources.avatarMagdalenaBanach.description,
                modifier = Modifier
                    .widthIn(min = 300.px, max = 500.px)
                    .fillMaxWidth()
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
        }
    }
}

@Composable
private fun AboutMeParagraphs() {
    aboutMeParagraphs.forEach { paragraph ->
        AppP(modifier = Modifier.textAlign(TextAlign.Justify)) { Text(paragraph.fixPolishOrphans()) }
    }
    AppP(modifier = Modifier.textAlign(TextAlign.Justify).fontWeight(FontWeight.Bold)) {
        Text(StringResources.MAGDALENA_BANACH_ABOUT_ME_CLOSING.fixPolishOrphans())
    }
}

@Composable
private fun SpecializationsSection() {
    val specializations = listOf(
        SpecialistData(
            icon = { FaCommentMedical() },
            title = StringResources.MAGDALENA_BANACH_SPEC_LOGOPEDIA_TITLE,
            description = StringResources.MAGDALENA_BANACH_SPEC_LOGOPEDIA_DESC
        ),
        SpecialistData(
            icon = { FaLanguage() },
            title = StringResources.MAGDALENA_BANACH_SPEC_DWUJEZYCZNOSC_TITLE,
            description = StringResources.MAGDALENA_BANACH_SPEC_DWUJEZYCZNOSC_DESC
        ),
        SpecialistData(
            icon = { FaUsers() },
            title = StringResources.MAGDALENA_BANACH_SPEC_SPE_TITLE,
            description = StringResources.MAGDALENA_BANACH_SPEC_SPE_DESC
        ),
        SpecialistData(
            icon = { FaBrain() },
            title = StringResources.MAGDALENA_BANACH_SPEC_PSYCHOLOGIA_TITLE,
            description = StringResources.MAGDALENA_BANACH_SPEC_PSYCHOLOGIA_DESC
        )
    )
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.MAGDALENA_BANACH_SPECIALIZATIONS_TITLE.fixPolishOrphans()) }
            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.5.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                specializations.forEach { info ->
                    SpecialistInfoCard(
                        icon = info.icon,
                        title = info.title,
                        description = info.description
                    )
                }
            }
        }
    }
}

@Composable
private fun ServicesSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.MAGDALENA_BANACH_SERVICES_TITLE.fixPolishOrphans()) }
            val services = listOf(
                StringResources.MAGDALENA_BANACH_SERVICE_1,
                StringResources.MAGDALENA_BANACH_SERVICE_2,
                StringResources.MAGDALENA_BANACH_SERVICE_3,
                StringResources.MAGDALENA_BANACH_SERVICE_4,
                StringResources.MAGDALENA_BANACH_SERVICE_5,
                StringResources.MAGDALENA_BANACH_SERVICE_6,
                StringResources.MAGDALENA_BANACH_SERVICE_7,
                StringResources.MAGDALENA_BANACH_SERVICE_8,
            )
            Ul {
                services.forEach { service ->
                    Li { Text(service.fixPolishOrphans()) }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.MAGDALENA_BANACH_PRICE_TITLE.fixPolishOrphans()) }

            Column(modifier = Modifier.gap(0.5.cssRem)) {
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 0.px)) { Text(StringResources.MAGDALENA_BANACH_PRICE_HEADER_1.fixPolishOrphans()) }
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 1.cssRem)) { Text(StringResources.MAGDALENA_BANACH_PRICE_HEADER_2.fixPolishOrphans()) }
            }

            SpecialistPriceTable(priceItems = priceItems)

            AppP(modifier = Modifier.fontSize(0.9.cssRem), color = SiteTheme.black._700) {
                Text(StringResources.MAGDALENA_BANACH_PRICE_NOTE.fixPolishOrphans())
            }
        }
    }
}
